import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";
const API_KEY = process.env.REACT_APP_API_KEY || "";

const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#EF4444", green: "#22C55E", amber: "#F59E0B", blue: "#3B82F6",
  rule: "#2A3048", teal: "#14B8A6", violet: "#8B5CF6", orange: "#F97316",
};

const STATUS_COLORS = {
  draft: "#64748B", submitted: C.blue, under_review: C.amber,
  approved: C.green, approved_as_noted: C.teal,
  rejected: C.red, revised: C.violet, superseded: "#6B7280",
};
const CHANGE_COLORS = { field_mod: C.orange, safety: C.red, ops: C.blue, tenant: C.violet, other: "#64748B" };
const SEV_COLORS = { critical: C.red, high: C.orange, medium: C.amber, low: "#64748B" };
const STATUSES = ["draft","submitted","under_review","approved","approved_as_noted","rejected","revised","superseded"];
const CHANGE_TYPES = ["field_mod","safety","ops","tenant","other"];
const QE_TYPES = ["scar","alert","ncr","field_failure","audit_finding","corrective_action","other"];
const SEV_OPTS = ["low","medium","high","critical"];

function Toast({ msg, onClose }) {
  if (!msg) return null;
  return <div style={{ position: "fixed", bottom: 24, right: 24, background: C.green, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>{msg}<button onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>x</button></div>;
}
function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "monospace", background: `${color}22`, color, border: `1px solid ${color}44` }}>{text}</span>;
}
function Field({ label, value }) {
  return <div style={{ marginBottom: 8 }}><div style={{ fontSize: 10, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, marginBottom: 2 }}>{label}</div><div style={{ fontSize: 13, color: C.text }}>{value || "—"}</div></div>;
}

export default function DesignManagement() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", ...(API_KEY ? { "X-API-Key": API_KEY } : {}), "X-User": activeUser.identifier };

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [changeLogDoc, setChangeLogDoc] = useState(null);
  const [qualityDoc, setQualityDoc] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [filters, setFilters] = useState({ vendor: "", sku: "", status: "", program: "" });
  const [toast, setToast] = useState(null);

  // Create modal state
  const [cEquip, setCEquip] = useState("");
  const [cVendor, setCVendor] = useState("");
  const [cProgram, setCProgram] = useState("");
  const [cProdId, setCProdId] = useState("");
  const [cSku, setCSku] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cRev, setCRev] = useState("Rev 0");
  const [cStatus, setCStatus] = useState("draft");
  const [cSp, setCSp] = useState("");
  const [cErr, setCErr] = useState(null);

  // Change log modal state
  const [clDesc, setClDesc] = useState("");
  const [clType, setClType] = useState("other");
  const [clAuthor, setClAuthor] = useState("");
  const [clErr, setClErr] = useState(null);

  // Quality event modal state
  const [qeType, setQeType] = useState("alert");
  const [qeTitle, setQeTitle] = useState("");
  const [qeDesc, setQeDesc] = useState("");
  const [qeSev, setQeSev] = useState("medium");
  const [qeDate, setQeDate] = useState(new Date().toISOString().slice(0, 10));
  const [qeErr, setQeErr] = useState(null);

  const fetchDocs = useCallback(() => {
    setLoading(true); setError(null);
    fetch(`${API}/procurement/design`, { headers: hdrs })
      .then(r => r.json()).then(d => { setDocs(d.design_docs || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [activeUser]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openDetail = async (docId) => {
    setDetailLoading(true); setSelectedDoc(null); setAnalysisResult(null);
    try {
      const r = await fetch(`${API}/procurement/design/${docId}`, { headers: hdrs });
      const d = await r.json();
      setSelectedDoc(d);
    } catch (e) { setError(e.message); }
    setDetailLoading(false);
  };

  const createDoc = async () => {
    if (!cEquip.trim()) { setCErr("Equipment type required"); return; }
    setCErr(null);
    try {
      await fetch(`${API}/procurement/design`, { method: "POST", headers: hdrs, body: JSON.stringify({ equipment_type: cEquip, vendor_id: null, program: cProgram || null, product_id: cProdId || null, sku: cSku || null, description: cDesc || null, current_revision: cRev || null, approval_status: cStatus, sharepoint_path: cSp || null }) });
      setCreateOpen(false); setCEquip(""); setCVendor(""); setCProgram(""); setCProdId(""); setCSku(""); setCDesc(""); setCRev("Rev 0"); setCStatus("draft"); setCSp("");
      setToast("Design doc created"); fetchDocs(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setCErr(e.message); }
  };

  const submitChangeLog = async () => {
    if (!clDesc.trim()) { setClErr("Description required"); return; }
    setClErr(null);
    try {
      await fetch(`${API}/procurement/design/${changeLogDoc.id}/change-log`, { method: "POST", headers: hdrs, body: JSON.stringify({ description: clDesc, change_type: clType, author: clAuthor || null }) });
      setChangeLogDoc(null); setClDesc(""); setClType("other"); setClAuthor("");
      setToast("Change logged \u2014 downstream impact flag created"); fetchDocs(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setClErr(e.message); }
  };

  const submitQuality = async () => {
    if (!qeTitle.trim()) { setQeErr("Title required"); return; }
    setQeErr(null);
    try {
      await fetch(`${API}/procurement/quality`, { method: "POST", headers: hdrs, body: JSON.stringify({ vendor_id: qualityDoc.vendor_id || null, doc_id: qualityDoc.id, sku: qualityDoc.sku || null, event_type: qeType, title: qeTitle, description: qeDesc || null, severity: qeSev, opened_date: qeDate || null, source_system: "manual" }) });
      setQualityDoc(null); setQeTitle(""); setQeDesc(""); setQeSev("medium"); setQeType("alert");
      setToast("Quality event logged"); if (selectedDoc) openDetail(selectedDoc.id); fetchDocs(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setQeErr(e.message); }
  };

  const runAnalysis = async () => {
    if (!selectedDoc) return;
    setAnalysisLoading(true); setAnalysisResult(null);
    const lastChange = (selectedDoc.design_changes || []).slice(-1)[0];
    try {
      const r = await fetch(`${API}/procurement/chat`, { method: "POST", headers: hdrs, body: JSON.stringify({ tool: "analyze_design_change", tool_params: { doc_id: selectedDoc.id, change_description: lastChange?.description || "Recent design change" }, message: "" }) });
      const d = await r.json();
      setAnalysisResult(d.result || d);
    } catch (e) { setAnalysisResult({ error: e.message }); }
    setAnalysisLoading(false);
  };

  const vendors = useMemo(() => [...new Set(docs.map(d => d.vendor_name).filter(Boolean))].sort(), [docs]);
  const filteredDocs = useMemo(() => {
    let r = docs;
    if (filters.vendor) r = r.filter(d => d.vendor_name === filters.vendor);
    if (filters.sku) r = r.filter(d => (d.sku || "").toLowerCase().includes(filters.sku.toLowerCase()));
    if (filters.status) r = r.filter(d => d.approval_status === filters.status);
    if (filters.program) r = r.filter(d => (d.program || "").toLowerCase().includes(filters.program.toLowerCase()));
    return r;
  }, [docs, filters]);

  const inp = { background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "monospace", outline: "none" };
  const sel = { ...inp, cursor: "pointer" };
  const btn = { background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, borderBottom: `1px solid ${C.rule}`, paddingBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Design Management</h1>
            <div style={{ fontSize: 12, color: C.textSec }}>{docs.length} docs | {vendors.length} vendors</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ProcurementUserSwitcher />
            <button onClick={() => setCreateOpen(true)} style={btn}>+ New Doc</button>
            <button onClick={fetchDocs} style={{ ...btn, background: C.card, color: C.textSec, border: `1px solid ${C.cardBorder}` }}>Refresh</button>
          </div>
        </div>

        {/* FILTER BAR */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <select value={filters.vendor} onChange={e => setFilters(f => ({ ...f, vendor: e.target.value }))} style={sel}>
            <option value="">All Vendors</option>
            {vendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <input value={filters.sku} onChange={e => setFilters(f => ({ ...f, sku: e.target.value }))} placeholder="SKU" style={{ ...inp, width: 120 }} />
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={sel}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <input value={filters.program} onChange={e => setFilters(f => ({ ...f, program: e.target.value }))} placeholder="Program" style={{ ...inp, width: 120 }} />
          <button onClick={() => setFilters({ vendor: "", sku: "", status: "", program: "" })} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Clear</button>
        </div>

        {loading && <div style={{ color: C.textDim, fontStyle: "italic", padding: 40, textAlign: "center" }}>Loading...</div>}
        {error && <div style={{ color: C.red, padding: 12, fontSize: 13 }}>{error}</div>}

        {/* EMPTY STATE */}
        {!loading && docs.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.textDim }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#x1F4C4;</div>
            <div style={{ fontSize: 15 }}>No design documents found.</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Add your first document with + New Doc</div>
          </div>
        )}

        {/* DOC GRID + DETAIL PANEL */}
        <div style={{ display: "flex", gap: 16 }}>
          {/* GRID */}
          <div style={{ flex: selectedDoc ? "0 0 60%" : 1, display: "grid", gridTemplateColumns: selectedDoc ? "1fr" : "1fr 1fr", gap: 12, alignContent: "start" }}>
            {filteredDocs.map(doc => {
              const sc = STATUS_COLORS[doc.approval_status] || "#64748B";
              const hasQe = (doc.open_quality_events || 0) > 0;
              const hasChanges = (doc.design_changes || []).length > 0;
              const canLogChange = ["approved", "approved_as_noted"].includes(doc.approval_status);
              return (
                <div key={doc.id} style={{ background: hasQe ? `${C.red}08` : C.card, border: `1px solid ${C.cardBorder}`, borderLeft: `3px solid ${sc}`, borderTop: hasChanges ? `2px solid ${C.amber}44` : undefined, borderRadius: 12, padding: "16px 20px" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                    <Badge text={doc.approval_status?.replace(/_/g, " ")} color={sc} />
                    {hasQe && <Badge text={`${doc.open_quality_events} QE`} color={C.red} />}
                    {doc.sku && <span style={{ fontFamily: "monospace", fontSize: 12, color: C.gold }}>{doc.sku}</span>}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>{doc.equipment_type || "Unnamed"}</div>
                  <div style={{ fontSize: 12, color: C.textSec }}>{doc.vendor_name || "No vendor"}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>{doc.program || ""}</div>
                  <div style={{ marginTop: 10, fontSize: 11, color: C.textSec }}>
                    <div>Rev {doc.current_revision || "—"} | {doc.approved_date || "Pending"}</div>
                    <div>{doc.weight_kg ? `${doc.weight_kg}kg` : "—"} | {doc.dims_lwh_cm || "—"} | {doc.units_per_truck ? `${doc.units_per_truck}/truck` : "—"}</div>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.storage_requirements || "—"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                    {hasChanges && <Badge text={`${(doc.design_changes || []).length} changes`} color={C.amber} />}
                    <div style={{ flex: 1 }} />
                    <button onClick={() => openDetail(doc.id)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>View Details</button>
                    {canLogChange && <button onClick={() => { setChangeLogDoc(doc); setClDesc(""); setClType("other"); setClAuthor(""); setClErr(null); }} style={{ background: "none", border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>Log Change</button>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* DETAIL PANEL */}
          {(selectedDoc || detailLoading) && (
            <div style={{ flex: "0 0 38%", background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "20px 24px", maxHeight: "calc(100vh - 100px)", overflowY: "auto", position: "sticky", top: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedDoc?.equipment_type || "Loading..."}</div>
                <button onClick={() => { setSelectedDoc(null); setAnalysisResult(null); }} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>&times;</button>
              </div>
              {detailLoading ? <div style={{ color: C.textDim }}>Loading...</div> : selectedDoc && (
                <>
                  {/* S1: Overview */}
                  <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.1em" }}>Overview</div>
                  <Badge text={selectedDoc.approval_status?.replace(/_/g, " ")} color={STATUS_COLORS[selectedDoc.approval_status] || "#64748B"} />
                  <div style={{ marginTop: 8 }}>
                    <Field label="Vendor" value={selectedDoc.vendor_name} />
                    <Field label="Program" value={selectedDoc.program} />
                    <Field label="Product ID" value={selectedDoc.product_id} />
                    <Field label="SKU" value={selectedDoc.sku} />
                    <Field label="Description" value={selectedDoc.description} />
                    <Field label="Revision" value={selectedDoc.current_revision} />
                    <Field label="Approved By" value={selectedDoc.approved_by} />
                    <Field label="Approved Date" value={selectedDoc.approved_date} />
                    {selectedDoc.sharepoint_path && <Field label="SharePoint" value={<a href={selectedDoc.sharepoint_path} target="_blank" rel="noreferrer" style={{ color: C.blue }}>{selectedDoc.sharepoint_path}</a>} />}
                  </div>
                  <div style={{ borderTop: `1px solid ${C.rule}`, margin: "12px 0" }} />

                  {/* S2: Logistics */}
                  <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>Logistics</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <Field label="Weight (kg)" value={selectedDoc.weight_kg} />
                    <Field label="Weight Packaged" value={selectedDoc.weight_kg_packaged} />
                    <Field label="Dims (cm)" value={selectedDoc.dims_lwh_cm} />
                    <Field label="Dims Packaged" value={selectedDoc.dims_lwh_cm_packaged} />
                    <Field label="Pallet Count" value={selectedDoc.pallet_piece_count} />
                    <Field label="Units/Truck" value={selectedDoc.units_per_truck} />
                  </div>
                  <Field label="Packaging Notes" value={selectedDoc.packaging_notes} />
                  <Field label="Storage Req" value={selectedDoc.storage_requirements} />
                  <Field label="Temp Range" value={selectedDoc.storage_temp_min_c != null ? `${selectedDoc.storage_temp_min_c}\u00B0C \u2013 ${selectedDoc.storage_temp_max_c}\u00B0C` : null} />
                  <div style={{ borderTop: `1px solid ${C.rule}`, margin: "12px 0" }} />

                  {/* S3: O&M */}
                  <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>O&M</div>
                  {selectedDoc.om_manual_url && <Field label="Manual" value={<a href={selectedDoc.om_manual_url} target="_blank" rel="noreferrer" style={{ color: C.blue }}>Link</a>} />}
                  <Field label="Received" value={selectedDoc.om_received ? "Yes" : "No"} />
                  <Field label="Received Date" value={selectedDoc.om_received_date} />
                  <div style={{ borderTop: `1px solid ${C.rule}`, margin: "12px 0" }} />

                  {/* S4: Files */}
                  <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>Files</div>
                  {(selectedDoc.files || []).length === 0 ? <div style={{ color: C.textDim, fontSize: 12 }}>No files uploaded yet.</div> : (
                    (selectedDoc.files || []).sort((a, b) => (b.is_latest ? 1 : 0) - (a.is_latest ? 1 : 0)).map((f, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${C.rule}`, fontSize: 12 }}>
                        <span style={{ color: C.text }}>{f.file_name}</span>
                        <Badge text={f.file_type} color={C.blue} />
                        {f.is_latest && <Badge text="latest" color={C.green} />}
                        <span style={{ color: C.textDim, fontSize: 10 }}>{f.revision}</span>
                      </div>
                    ))
                  )}
                  <div style={{ borderTop: `1px solid ${C.rule}`, margin: "12px 0" }} />

                  {/* S5: Design Changes */}
                  <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>Design Changes</div>
                  {(selectedDoc.design_changes || []).length > 0 && <div style={{ background: `${C.amber}15`, border: `1px solid ${C.amber}33`, borderRadius: 6, padding: "4px 10px", fontSize: 11, color: C.amber, fontWeight: 600, marginBottom: 8 }}>CHANGES LOGGED</div>}
                  {(selectedDoc.design_changes || []).length === 0 ? <div style={{ color: C.textDim, fontSize: 12 }}>No design changes recorded.</div> : (
                    (selectedDoc.design_changes || []).map((ch, i) => (
                      <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${C.rule}`, fontSize: 12 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ color: C.textDim, fontSize: 10 }}>{ch.date?.slice(0, 10)}</span>
                          <Badge text={ch.change_type} color={CHANGE_COLORS[ch.change_type] || "#64748B"} />
                          <span style={{ color: C.textSec, fontSize: 11 }}>{ch.author}</span>
                        </div>
                        <div style={{ color: C.text, marginTop: 2 }}>{ch.description}</div>
                      </div>
                    ))
                  )}
                  <div style={{ borderTop: `1px solid ${C.rule}`, margin: "12px 0" }} />

                  {/* S6: Quality Events */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase" }}>Quality Events</div>
                    <button onClick={() => { setQualityDoc(selectedDoc); setQeTitle(""); setQeDesc(""); setQeSev("medium"); setQeType("alert"); setQeDate(new Date().toISOString().slice(0, 10)); setQeErr(null); }} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer" }}>+ Log Issue</button>
                  </div>
                  {(selectedDoc.quality_events || []).filter(e => ["open","in_progress"].includes(e.status)).length === 0 && <div style={{ color: C.green, fontSize: 12, marginBottom: 4 }}>No open quality issues.</div>}
                  {(selectedDoc.quality_events || []).map((qe, i) => (
                    <div key={i} style={{ padding: "4px 0", borderBottom: `1px solid ${C.rule}`, fontSize: 12 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <Badge text={qe.severity} color={SEV_COLORS[qe.severity] || "#64748B"} />
                        <span style={{ color: C.textSec }}>{qe.event_type}</span>
                        <Badge text={qe.status} color={qe.status === "open" ? C.red : qe.status === "in_progress" ? C.amber : C.green} />
                      </div>
                      <div style={{ color: C.text, marginTop: 2 }}>{qe.title}</div>
                      <div style={{ color: C.textDim, fontSize: 10 }}>{qe.opened_date}</div>
                    </div>
                  ))}
                  <div style={{ borderTop: `1px solid ${C.rule}`, margin: "12px 0" }} />

                  {/* S7: AI Analysis */}
                  {(selectedDoc.design_changes || []).length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontFamily: "monospace", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>AI Design Change Analysis</div>
                      <button onClick={runAnalysis} disabled={analysisLoading} style={{ ...btn, marginBottom: 10 }}>{analysisLoading ? "Analyzing impact..." : "Analyze Downstream Impact"}</button>
                      {analysisResult && !analysisResult.error && (
                        <div>
                          <div style={{ fontStyle: "italic", color: C.text, fontSize: 13, lineHeight: 1.6, padding: 12, background: `${C.gold}08`, borderRadius: 8, borderLeft: `3px solid ${C.gold}`, marginBottom: 8 }}>{analysisResult.impact_narrative}</div>
                          <div style={{ fontSize: 12, color: C.textSec }}>Affected: {analysisResult.affected_unit_count} units across {(analysisResult.affected_projects || []).length} projects | {analysisResult.open_submittal_count} open submittals</div>
                        </div>
                      )}
                      {analysisResult?.error && <div style={{ color: C.amber, fontSize: 12 }}>{analysisResult.error}</div>}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* CREATE MODAL */}
      {createOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setCreateOpen(false)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 480, maxWidth: "90vw", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>New Design Document</div>
            {[["Equipment Type *", cEquip, setCEquip], ["Vendor", cVendor, setCVendor], ["Program", cProgram, setCProgram], ["Product ID", cProdId, setCProdId], ["SKU", cSku, setCSku], ["Current Revision", cRev, setCRev], ["SharePoint Path", cSp, setCSp]].map(([l, v, s]) => (
              <div key={l} style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>{l}</label><input value={v} onChange={e => s(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            ))}
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Description</label><textarea value={cDesc} onChange={e => setCDesc(e.target.value)} rows={3} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Status</label><select value={cStatus} onChange={e => setCStatus(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
            {cErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{cErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setCreateOpen(false)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={createDoc} style={btn}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE LOG MODAL */}
      {changeLogDoc && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setChangeLogDoc(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 420, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Log Design Change</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{changeLogDoc.sku || changeLogDoc.equipment_type} | Doc #{changeLogDoc.id}</div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Description *</label><textarea value={clDesc} onChange={e => setClDesc(e.target.value)} rows={3} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Change Type *</label><select value={clType} onChange={e => setClType(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{CHANGE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select></div>
            <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Author</label><input value={clAuthor} onChange={e => setClAuthor(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            {clErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{clErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setChangeLogDoc(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitChangeLog} style={btn}>Log Change</button>
            </div>
          </div>
        </div>
      )}

      {/* QUALITY EVENT MODAL */}
      {qualityDoc && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setQualityDoc(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 420, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Log Quality Event</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{qualityDoc.sku || qualityDoc.equipment_type}</div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Event Type *</label><select value={qeType} onChange={e => setQeType(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{QE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Title *</label><input value={qeTitle} onChange={e => setQeTitle(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Description</label><textarea value={qeDesc} onChange={e => setQeDesc(e.target.value)} rows={2} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} /></div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Severity</label><select value={qeSev} onChange={e => setQeSev(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{SEV_OPTS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Opened Date</label><input type="date" value={qeDate} onChange={e => setQeDate(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            </div>
            {qeErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{qeErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setQualityDoc(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitQuality} style={btn}>Log Event</button>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast} onClose={() => setToast(null)} />
    </div>
  );
}
